import React, { memo, useCallback, useMemo, useState } from 'react';
import { View } from 'react-native';

import { useUserContext } from '../../../../common/utils/context';
import { SiteGroupHeader, StopRow } from '../../../design/components';
import { STEP_STATUS } from '../../../design/components/StatusIcon';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import {
	getPositionWarnings,
	getSiteImportantNotes,
	getSiteWarnings,
	getStopAddress,
	getStopLabels,
	getStopMachineDetails,
	getStopPositionName,
	getStopServiceLabels,
	getStopSiteName,
	groupStopsBySite,
} from '../stopDetails';

const getCollapsedLabel = (count) => {
	if (count === 1) {
		return 'Machine done';
	}

	return count === 2 ? 'Both machines done' : `All ${count} machines done`;
};

const RunStopRow = memo(({
	docket,
	number,
	isDone,
	isNow,
	onOpenStop,
	tenantSettings,
	showWarnings,
	showCompletedDetails,
}) => {
	const status = isDone ? STEP_STATUS.DONE : isNow ? STEP_STATUS.CURRENT : STEP_STATUS.TODO;
	// A completed stop is simply complete, even if it was marked before.
	const couldNotComplete = !isDone && Boolean(docket.couldNotComplete?.at);
	// Classic run-list details: service, restock type, model, wrap, type and key.
	const details = useMemo(() => [
		...getStopServiceLabels(docket, tenantSettings),
		getStopMachineDetails(docket),
		!isDone && !couldNotComplete && docket.startedProcessing ? 'In progress' : null,
	].filter(Boolean).join(' · '), [couldNotComplete, docket, isDone, tenantSettings]);
	const labels = useMemo(() => [
		couldNotComplete && { label: 'Couldn\'t complete', tone: 'danger' },
		...getStopLabels(docket, tenantSettings),
	], [couldNotComplete, docket, tenantSettings]);
	const warnings = useMemo(() => (showWarnings && !isDone ? getPositionWarnings(docket) : undefined), [docket, isDone, showWarnings]);
	const onPress = useCallback(() => onOpenStop(docket), [docket, onOpenStop]);

	return (
		<StopRow
			title={getStopPositionName(docket)}
			machineNumber={docket.machine?.assetId}
			details={details}
			showCompletedDetails={showCompletedDetails}
			labels={labels}
			status={status}
			number={number}
			warnings={warnings}
			onPress={onOpenStop ? onPress : undefined}
			accessibilityHint={onOpenStop ? 'Opens the stop' : undefined}
		/>
	);
});

const RunSiteGroup = memo(({
	group,
	doneIds,
	nowDocketId,
	isCollapsed,
	canCollapse,
	onToggleGroup,
	onOpenStop,
	tenantSettings,
	showWarnings,
	showStopNumbers,
	showCompletedDetails,
}) => {
	const styles = useThemedStyles(createStyles);
	const firstDocket = group.dockets[0].docket;
	const doneCount = group.dockets.filter(({ docket }) => doneIds.has(docket.id)).length;
	const totalCount = group.dockets.length;
	const showSiteWarnings = showWarnings && doneCount < totalCount;
	const siteWarnings = useMemo(() => (
		showSiteWarnings ? getSiteWarnings(group.location) : undefined
	), [group.location, showSiteWarnings]);
	const siteImportantNotes = useMemo(() => (
		showSiteWarnings ? getSiteImportantNotes(group.location) : undefined
	), [group.location, showSiteWarnings]);
	const onToggle = useCallback(() => onToggleGroup(group.key, !isCollapsed), [group.key, isCollapsed, onToggleGroup]);

	return (
		<View style={styles.group}>
			<SiteGroupHeader
				name={getStopSiteName(firstDocket) || getStopPositionName(firstDocket)}
				address={getStopAddress(firstDocket)}
				doneCount={doneCount}
				totalCount={totalCount}
				warnings={siteWarnings}
				importantNotes={siteImportantNotes}
				isCollapsed={isCollapsed}
				onToggle={canCollapse ? onToggle : undefined}
				collapsedLabel={getCollapsedLabel(totalCount)}
			/>
			{!isCollapsed && group.dockets.map(({ docket, number }) => (
				<RunStopRow
					key={docket.id}
					docket={docket}
					number={showStopNumbers ? number : undefined}
					isDone={doneIds.has(docket.id)}
					isNow={docket.id === nowDocketId}
					onOpenStop={onOpenStop}
					tenantSettings={tenantSettings}
					showWarnings={showWarnings}
					showCompletedDetails={showCompletedDetails}
				/>
			))}
		</View>
	);
});

/**
 * RunStopsList: the run's stops in route order, grouped by site (consecutive
 * stops at one site share a SiteGroupHeader with n/m done and the site's
 * requirements). Finished sites start collapsed to one line and can be opened;
 * position-level requirements are joined to the top of their stop's row.
 * Used by On the run (RunListView) and the Run overview of Start of day.
 * Props:
 * - dockets: the run's service dockets in route order (useDriverDay().sortedDockets)
 * - isDocketDone(docket): completion test (useDriverDay().isDocketDone)
 * - nowDocketId: id of the stop to mark "Now" (current outline), or null
 * - onOpenStop(docket): row press; omit for a read-only list
 * - collapseDoneSites (default true): finished sites start collapsed
 * - showWarnings (default true): site and position requirement bars
 * - showStopNumbers: show each stop's route number (1, 2, 3...) in place of its status circle
 * - showCompletedDetails (default false): retain service details on completed history stops
 * - style
 */
const RunStopsList = ({
	dockets,
	isDocketDone,
	nowDocketId = null,
	onOpenStop,
	collapseDoneSites = true,
	showWarnings = true,
	showStopNumbers = false,
	showCompletedDetails = false,
	style,
}) => {
	const styles = useThemedStyles(createStyles);
	const { tenantSettings } = useUserContext();
	// Per-site manual open/close; finished sites otherwise follow collapseDoneSites.
	const [collapsedOverrides, setCollapsedOverrides] = useState({});

	const groups = useMemo(() => groupStopsBySite(dockets), [dockets]);
	const doneIds = useMemo(() => new Set((dockets || []).filter(isDocketDone).map(docket => docket.id)), [dockets, isDocketDone]);
	const onToggleGroup = useCallback((groupKey, nextCollapsed) => {
		setCollapsedOverrides(overrides => ({ ...overrides, [groupKey]: nextCollapsed }));
	}, []);

	return (
		<View style={[styles.list, style]}>
			{groups.map((group) => {
				const isGroupDone = group.dockets.every(({ docket }) => doneIds.has(docket.id));
				const isCollapsed = isGroupDone && (collapsedOverrides[group.key] ?? collapseDoneSites);

				return (
					<RunSiteGroup
						key={group.key}
						group={group}
						doneIds={doneIds}
						nowDocketId={nowDocketId}
						isCollapsed={isCollapsed}
						canCollapse={isGroupDone}
						onToggleGroup={onToggleGroup}
						onOpenStop={onOpenStop}
						tenantSettings={tenantSettings}
						showWarnings={showWarnings}
						showStopNumbers={showStopNumbers}
						showCompletedDetails={showCompletedDetails}
					/>
				);
			})}
		</View>
	);
};

const createStyles = ({ ui: { space } }) => ({
	list: {
		gap: space.xl,
	},
	group: {
		gap: space.sm,
	},
});

export default RunStopsList;
